import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import DeleteIcon from "@mui/icons-material/Delete";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import LoopIcon from "@mui/icons-material/Loop";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import { IconButton, type SxProps } from "@mui/material";
import type { FC, MouseEvent } from "react";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { useIsWalkthroughAnchorActive } from "../../../UIToolkit/Walkthrough/walkthroughRunning";
import { DailyGapsQuickAction } from "./utils";

/** `data-testid` per action, e.g. `daily-gaps-quick-action-edit-notes` — QA hook, no behaviour change. */
const quickActionTestId = (action: DailyGapsQuickAction): string => `daily-gaps-quick-action-${action.toLowerCase().replace(/\s+/g, "-")}`;

type Props = {
	quickAction: DailyGapsQuickAction;
	onClick: (e: MouseEvent<HTMLElement>, action: DailyGapsQuickAction) => void;
	isHovered: boolean;
	/**
	 * This is THE control the call-off walkthrough spotlights and presses (`day-schedule-call-off-trigger`).
	 * Shown at full opacity while the call-off tour runs, instead of only on hover — a spotlight on an invisible button
	 * would point at nothing. The name stays on at rest: the launch scan needs it before the tour starts.
	 */
	isCallOffWalkthroughAnchor: boolean;
};

const iconSx = { fontSize: "12px" } as const satisfies SxProps;

// The call-off tour's trigger step. The JSX attribute below stays a literal: the anchor lint
// (`check:annie-knowledge`) finds placements by the quoted name on `data-annie-target`.
const CALL_OFF_TRIGGER_ANCHOR = "day-schedule-call-off-trigger";

const IconForQuickAction: FC<{ action: DailyGapsQuickAction }> = ({ action }) => {
	switch (action) {
		case DailyGapsQuickAction.LOCATION:
			return <EditLocationIcon sx={iconSx} />;
		case DailyGapsQuickAction.TIME:
			return <AccessTimeFilledIcon sx={iconSx} />;
		case DailyGapsQuickAction.NOTE:
			return <NoteAltIcon sx={iconSx} />;
		case DailyGapsQuickAction.REMOVE:
			return <DeleteIcon sx={iconSx} />;
		case DailyGapsQuickAction.EDIT_RECURRING:
			return <LoopIcon sx={iconSx} />;
		default:
			return "❓";
	}
};

export const DailyGapsQuickActionButton: FC<Props> = ({ quickAction, onClick, isHovered, isCallOffWalkthroughAnchor }) => {
	// Only while a tour that spotlights this control runs: otherwise every department with the call-off agent
	// on would see one lone trash icon at rest on the first qualifying block, with its siblings hover-only.
	const isSpotlitByTour = useIsWalkthroughAnchorActive(CALL_OFF_TRIGGER_ANCHOR);
	return (
		<InHouseTooltip placement={"top"} title={quickAction}>
			<IconButton
				data-testid={quickActionTestId(quickAction)}
				onClick={(event) => onClick(event, quickAction)}
				size={"small"}
				data-annie-target={isCallOffWalkthroughAnchor ? "day-schedule-call-off-trigger" : undefined}
				sx={{
					opacity: isHovered || (isCallOffWalkthroughAnchor && isSpotlitByTour) ? 1 : 0,
					transition: "opacity 0.2s",
				}}
			>
				<IconForQuickAction action={quickAction} />
			</IconButton>
		</InHouseTooltip>
	);
};
